import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiError } from "../lib/api";
import { Inbox, DollarSign, CheckCircle2, XCircle, Loader2, Building2, FileSignature } from "lucide-react";
import { toast } from "sonner";

const STATUS_FILTERS = ["Pending", "Approved", "Denied", "all"];

export default function RequestsInbox() {
  const [data, setData] = useState({ counts: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Pending");
  const [decidingId, setDecidingId] = useState(null);
  const [notes, setNotes] = useState({});
  const [approveAmount, setApproveAmount] = useState({});

  useEffect(() => { load(); }, [filter]);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/operations/funding-requests`, { params: filter === "all" ? {} : { status: filter } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const decide = async (request_id, decision) => {
    setDecidingId(request_id);
    try {
      const body = {
        decision,
        decision_notes: notes[request_id] || "",
        approved_amount: decision === "approved" && approveAmount[request_id] ? Number(approveAmount[request_id]) : null,
      };
      await api.post(`/operations/funding-requests/${request_id}/decision`, body);
      toast.success(decision === "approved" ? "Request approved" : "Request denied");
      setNotes((n) => ({ ...n, [request_id]: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDecidingId(null); }
  };

  const totalPending = data.counts?.pending || 0;

  return (
    <div className="p-6 md:p-8 max-w-[1300px] mx-auto" data-testid="requests-inbox">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#6B7280] mb-1">
            <Inbox className="w-3.5 h-3.5" /> Requests
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Attorney inbox</h1>
          <p className="text-sm text-[#6B7280] mt-1">{totalPending} pending · {data.counts?.approved || 0} approved · {data.counts?.denied || 0} denied</p>
        </div>
      </div>

      <div className="flex items-center gap-1 p-1 rounded-lg bg-[#F3F4F6] inline-flex mb-5" data-testid="requests-filter">
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
            className={`px-3 h-8 rounded-md text-xs font-medium transition-colors capitalize ${
              filter === s ? "bg-white text-[#0A2540] shadow-sm" : "text-[#6B7280] hover:text-[#0A2540]"
            }`}>{s === "all" ? "All" : s}</button>
        ))}
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#6B7280] inline-flex items-center gap-2 w-full justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : data.items.length === 0 ? (
        <div className="p-16 bg-white border border-[#E5E7EB] rounded-lg text-center" data-testid="requests-empty">
          <div className="inline-flex flex-col items-center text-[#6B7280]">
            <Inbox className="w-7 h-7 mb-2 text-[#9CA3AF]" />
            <div className="text-sm">No {filter !== "all" ? filter.toLowerCase() : ""} requests.</div>
          </div>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.items.map((r) => {
            const isPending = r.status === "Pending";
            const tone = r.status === "Approved" ? { bg: "#DCFCE7", text: "#166534" }
              : r.status === "Denied" ? { bg: "#FEE2E2", text: "#B91C1C" }
              : { bg: "#FEF3C7", text: "#D97706" };
            return (
              <li key={r.request_id} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden" data-testid={`request-${r.request_id}`}>
                <div className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] text-[#1D4ED8] flex items-center justify-center flex-shrink-0">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-[#0A2540] flex items-center gap-2">
                        {formatCurrency(r.amount_requested)}
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: tone.bg, color: tone.text }}>{r.status}</span>
                      </div>
                      <div className="text-xs text-[#6B7280] flex items-center gap-2 mt-0.5 flex-wrap">
                        <Link to={`/admin/loans/${r.loan_id}`} className="text-[#0066FF] hover:underline inline-flex items-center gap-1"><FileSignature className="w-3 h-3" /> {r.client_name}</Link>
                        <span>·</span>
                        <Link to={`/admin/firms/${r.firm_id}`} className="inline-flex items-center gap-1 hover:text-[#0A2540]"><Building2 className="w-3 h-3" /> {r.firm_name}</Link>
                        <span>· {r.attorney_email}</span>
                        <span>· {formatDate(r.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="px-5 pb-3">
                  <p className="text-sm text-[#374151] leading-relaxed">{r.reason}</p>
                  {(r.medical_update || r.timeline_update) && (
                    <div className="grid sm:grid-cols-2 gap-2 mt-3 text-xs text-[#6B7280]">
                      {r.medical_update && <div><span className="font-medium text-[#0A2540]">Medical: </span>{r.medical_update}</div>}
                      {r.timeline_update && <div><span className="font-medium text-[#0A2540]">Timeline: </span>{r.timeline_update}</div>}
                    </div>
                  )}
                </div>
                {isPending && (
                  <div className="px-5 py-3 bg-[#F9FAFB] border-t border-[#F3F4F6]">
                    <div className="grid sm:grid-cols-[1fr_140px_auto_auto] gap-2 items-center">
                      <input
                        value={notes[r.request_id] || ""}
                        onChange={(e) => setNotes((n) => ({ ...n, [r.request_id]: e.target.value }))}
                        placeholder="Optional decision notes (sent to firm)…"
                        className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                        data-testid={`decision-notes-${r.request_id}`}
                      />
                      <input
                        inputMode="decimal"
                        value={approveAmount[r.request_id] || ""}
                        onChange={(e) => setApproveAmount((a) => ({ ...a, [r.request_id]: e.target.value.replace(/[^0-9.]/g, "") }))}
                        placeholder={`Approve $${r.amount_requested}`}
                        className="h-9 px-3 rounded-md border border-[#E5E7EB] text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-[#0066FF]/20 focus:border-[#0066FF]"
                        data-testid={`approve-amount-${r.request_id}`}
                      />
                      <button
                        onClick={() => decide(r.request_id, "denied")}
                        disabled={decidingId === r.request_id}
                        className="h-9 px-3 rounded-md border border-[#FCA5A5] text-[#B91C1C] hover:bg-[#FEF2F2] text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid={`deny-${r.request_id}`}
                      >
                        <XCircle className="w-4 h-4" /> Deny
                      </button>
                      <button
                        onClick={() => decide(r.request_id, "approved")}
                        disabled={decidingId === r.request_id}
                        className="h-9 px-3 rounded-md bg-[#047857] hover:bg-[#065F46] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid={`approve-${r.request_id}`}
                      >
                        {decidingId === r.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approve
                      </button>
                    </div>
                  </div>
                )}
                {!isPending && r.decision_notes && (
                  <div className="px-5 py-3 bg-[#F9FAFB] border-t border-[#F3F4F6] text-xs text-[#6B7280]">
                    <span className="font-medium text-[#0A2540]">Decision: </span>{r.decision_notes}
                    {r.decided_by && <span> · by {r.decided_by} · {formatDate(r.decided_at)}</span>}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
